import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { find, includes } from 'lodash';
import {
  getCarriedForwardScorepoints,
  getScorepointShuffleType,
  shuffleArray,
} from '../../../utils';
import {
  QUESTION_MATRIX_MULTIPLE,
  SHUFFLE_MATRICES_SAME_ORDER,
  SHUFFLE_REVERSE,
  SHUFFLE_REVERSE_WITH_LAST_FIXED,
  SHUFFLE_REVERSE_WITH_LAST_TWO_FIXED,
} from '../../../constants';
import { getAnswerClass, removeScorepointFromAnswerArray } from './utils';
import { shuffleAnswers } from '../../Answers/utils';

export default function Matrix({ question = {}, getText }) {
  const { answers, shuffle: shuffleType } = question;
  const [selectedScorepoints, setScorepoints] = useState([]);
  const columns = useMemo(() => {
    const transformedScorepoints = getCarriedForwardScorepoints(question);
    let newScorepoints;
    // SHUFFLE_MATRICES_SAME_ORDER is not currently implemented in the backend, and will not run
    if (shuffleType === SHUFFLE_MATRICES_SAME_ORDER) {
      newScorepoints = shuffleArray(
        transformedScorepoints,
        getScorepointShuffleType(shuffleType),
      );
    }

    // At the moment, the selected answers are kept in DOM, as indexes
    // and we get the scorePoint id based on the index of the array question.scorepoints
    // But now, every answer can have different order on the scorepoint array,
    // so we override this array, and push every answer's ordering
    // eslint-disable-next-line
    newScorepoints = [];

    let data;
    answers.forEach((_, idx) => {
      if (shuffleType === SHUFFLE_MATRICES_SAME_ORDER) {
        data = transformedScorepoints;
      } else {
        const normalizedShuffleType = getScorepointShuffleType(shuffleType);
        const hasReverseShuffleType = includes(
          [
            SHUFFLE_REVERSE,
            SHUFFLE_REVERSE_WITH_LAST_FIXED,
            SHUFFLE_REVERSE_WITH_LAST_TWO_FIXED,
          ],
          normalizedShuffleType,
        );

        // for reverse shuffle, do the shuffling only once so that the
        // order of scorepoints for each answer is the same
        if (!hasReverseShuffleType || (hasReverseShuffleType && idx === 0)) {
          data = shuffleArray(transformedScorepoints, normalizedShuffleType);
        }
      }
      // shuffle scorepoints per answer
      newScorepoints.push([...data]);
    });
    return newScorepoints;
  }, [answers, question, shuffleType]);

  const rows = useMemo(() => shuffleAnswers(question), [question]);

  const onHandleMatrixAnswerTap = useCallback(
    ({ target }) => {
      let newSelectedScorepoints = [...selectedScorepoints];
      const selectedScorepointElement = $(target).closest('.matrix__answer');
      const selectedScorepointId = selectedScorepointElement.data(
        'scorepoint-id',
      );
      const answerId = selectedScorepointElement.attr('data-answer-id');
      const answerElement = $(`.answerMatrix[data-answer-id=${answerId}]`);
      const answersContainer = answerElement.find('.matrix__answers');

      const selectedLabel = answerElement.find(
        `.matrix__selected-answer[data-scorepoint-id=${selectedScorepointId}]`,
      );

      const scorepointIdsToRemove = [];
      // we can select only one exclusive answer per question
      // this feature is allowed only in multiple select
      if (selectedScorepointElement.hasClass('exclusive-answer')) {
        // unselect selected answers
        const $selectedSiblings = selectedScorepointElement.siblings(
          '.answer--click',
        );
        $selectedSiblings.each(function removeScorepoint() {
          const scorepointId = $(this).attr('data-scorepoint-id');
          scorepointIdsToRemove.push(scorepointId);
        });
      } else if (selectedScorepointElement.hasClass('answer--multi-choice')) {
        const $selectedExclusiveSiblings = selectedScorepointElement.siblings(
          '.exclusive-answer.answer--click',
        );
        $selectedExclusiveSiblings.each(function removeScorepoint() {
          const scorepointId = $(this).attr('data-scorepoint-id');
          scorepointIdsToRemove.push(scorepointId);
        });
      }

      scorepointIdsToRemove.forEach((scorepointId) => {
        newSelectedScorepoints = removeScorepointFromAnswerArray(
          scorepointId,
          newSelectedScorepoints,
          answerId,
        );
      });

      if (selectedScorepointElement.hasClass('answer--multi-choice')) {
        selectedScorepointElement.toggleClass('answer--click');
        selectedLabel.toggleClass('matrix__selected-answer--active');

        if (selectedScorepointElement.hasClass('answer--click')) {
          const selectedAnswer = find(newSelectedScorepoints, [
            'answerId',
            answerId,
          ]);
          if (!selectedAnswer) {
            newSelectedScorepoints.push({
              answerId,
              scorepointIds: [selectedScorepointId],
            });
          } else {
            selectedAnswer.scorepointIds.push(selectedScorepointId);
          }
        } else {
          newSelectedScorepoints = removeScorepointFromAnswerArray(
            selectedScorepointId,
            newSelectedScorepoints,
            answerId,
          );
        }

        const answer = find(newSelectedScorepoints, ['answerId', answerId]);
        const selectedScorepointIds = answer.scorepointIds;
        answerElement.attr(
          'data-selected-scorepoint-id',
          selectedScorepointIds.join(','),
        );
      } else {
        selectedScorepointElement.siblings().removeClass('answer--click');
        selectedScorepointElement.addClass('answer--click');
        answerElement.attr('data-selected-scorepoint-id', selectedScorepointId);
        answerElement
          .find('.matrix__labels span')
          .removeClass('matrix__selected-answer--active');
        selectedLabel.addClass('matrix__selected-answer--active');
        answersContainer.addClass('matrix__answers--closed').slideUp();
      }
      setScorepoints(newSelectedScorepoints);
    },
    [selectedScorepoints, setScorepoints],
  );
  const onHandleMatrixQuestionTap = useCallback((e) => {
    const { target: selectedQuestion } = e;
    const target = $(selectedQuestion)
      .parents('.answerMatrix')
      .attr('data-answer-id');
    const answersDiv = $(`.matrix__answers[data-answer-id=${target}]`);
    const matrix = $(`.matrix[data-answer-id=${target}]`);

    if (answersDiv.hasClass('matrix__answers--closed')) {
      answersDiv.removeClass('matrix__answers--closed').slideDown();
      matrix.removeClass('matrix--closed');
    } else {
      answersDiv.addClass('matrix__answers--closed').slideUp();
      matrix.addClass('matrix--closed');
    }
  }, []);
  const onHandleMatrixHideAnswersButton = useCallback((e) => {
    e.preventDefault();
    const answerId = $(e.target).attr('data-answer-id');

    $(`.matrix__answers[data-answer-id=${answerId}]`)
      .addClass('matrix__answers--closed')
      .slideUp();
    $(`.matrix[data-answer-id=${answerId}]`).addClass('matrix--closed');
  }, []);

  return (
    <div className="matrix-wrapper">
      {rows.map((row, index) => {
        const rowColumns = columns[index];
        return (
          <div
            className="matrix clearfix answerMatrix"
            data-answer-id={row.id}
            key={row.id}
          >
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
            <div
              className="matrix__question"
              onClick={onHandleMatrixQuestionTap}
            >
              <span
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: row.body || '&nbsp;' }}
              />
              <div className="matrix__labels">
                {rowColumns.map((column) => (
                  <span
                    key={`label-${column.id}`}
                    className="matrix__selected-answer"
                    data-scorepoint-id={column.id}
                    data-answer-id={row.id}
                  >
                    <span
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: column.body || '&nbsp;',
                      }}
                    />
                  </span>
                ))}
              </div>
            </div>
            <div className="matrix__answers" data-answer-id={row.id}>
              {rowColumns.map((column) => (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events
                <div
                  key={column.id}
                  data-answer-id={row.id}
                  data-scorepoint-id={column.id}
                  className={`matrix__answer ${getAnswerClass({
                    question,
                    column,
                  })}`}
                  onClick={onHandleMatrixAnswerTap}
                >
                  <span
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: column.body || '&nbsp;',
                    }}
                  />
                </div>
              ))}
              {question.style === QUESTION_MATRIX_MULTIPLE && (
                <div className="text-center">
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a
                    href="#"
                    data-answer-id={row.id}
                    className="matrix__hide-answers-btn"
                    onClick={onHandleMatrixHideAnswersButton}
                  >
                    {getText('hide_answers_multimatrix_prompt')}
                  </a>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

Matrix.propTypes = {
  question: PropTypes.object,
  getText: PropTypes.func.isRequired,
};
